// The language the page is written in, and the words of the browser's own code in it.
//
// The server decides the language (config/PathLocaleResolver: the address, or the account) and writes it on
// <html lang>, which this reads the moment it is imported, before any page has drawn anything. The words live in i18n/<area>.ts, each
// area with its English and its Spanish side by side, through words(): the type demands that every language
// has every key with the same arguments, so a missing translation fails `npm run verify` and never reaches a
// page. The one look at the DOM is that read, guarded, so domain/ can build sentences with it and its tests
// run in English under Node.

export type Lang = "en" | "es";

const LANGS: readonly Lang[] = ["en", "es"];

let current: Lang = "en";

/** Anything it does not know is English. */
export function setLanguage(lang: string | null | undefined): void {
    const code = (lang || "").slice(0, 2).toLowerCase() as Lang;
    current = LANGS.includes(code) ? code : "en";
}

if (typeof document !== "undefined") setLanguage(document.documentElement.lang);

export function language(): Lang {
    return current;
}

/** A word, or a sentence that needs something of the moment: a number, a name. */
type Entry = string | ((...args: never[]) => string);

type Args<E> = E extends (...args: infer P) => string ? P : [];

/**
 * The words of one area of the site. English is the reference: Spanish must have every key, and a sentence
 * must take what the English one takes.
 */
export function words<D extends Record<string, Entry>>(byLang: { en: D; es: { [K in keyof D]: D[K] extends string ? string : D[K] } }) {
    return function t<K extends keyof D>(key: K, ...args: Args<D[K]>): string {
        const entry = (byLang[current] as D)[key] ?? byLang.en[key];
        return typeof entry === "function" ? (entry as (...a: unknown[]) => string)(...args) : (entry as string);
    };
}

/**
 * The language the reader chose on the front page, for the pages that have none in their address and for the
 * account created next (web/LanguagePreference, the same cookie the server sets when /es is read).
 */
export function rememberLanguage(lang: string): void {
    if (typeof document === "undefined") return;
    document.cookie = `sparring_lang=${encodeURIComponent(lang)}; path=/; max-age=${365 * 24 * 3600}; samesite=lax`;
}

/** A number as the language writes it: 4.4 in English, 4,4 in Spanish. */
export function decimal(value: number, digits = 1): string {
    return value.toLocaleString(current === "es" ? "es-ES" : "en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
}

/** "one game", "3 games": the noun chosen by the count, the count written as the language writes numbers. */
export function count(n: number, one: string, many: string): string {
    return `${n.toLocaleString(current === "es" ? "es-ES" : "en-US")} ${n === 1 ? one : many}`;
}
